import React, { useState, useEffect } from 'react';
import API from '../api';

const PAGE_SIZE = 10;

const STATUS_INFO = {
    SENT: { label: 'Poslato', color: '#64748b' },
    DELIVERED: { label: 'Primljeno', color: '#10b981' },
    UNDELIVERED: { label: 'Greška', color: '#e74c3c' },
    FAILED: { label: 'Greška', color: '#e74c3c' }
};

const statusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: (STATUS_INFO[status] || STATUS_INFO.FAILED).color
});

const channelStyle = (channel) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: channel === 'VIBER' ? '#7c3aed' : '#1c8aa6'
});

const typeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#475569',
    backgroundColor: '#e2e8f0'
};

const isFailed = (status) => status === 'UNDELIVERED' || status === 'FAILED';

const SmsLogPanel = () => {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshResult, setRefreshResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [retryingId, setRetryingId] = useState(null);
    const [retryingSelected, setRetryingSelected] = useState(false);
    const [retryResult, setRetryResult] = useState(null);

    const fetchPage = async (pageNumber) => {
        try {
            const res = await API.get('/sms-logs', { params: { page: pageNumber, size: PAGE_SIZE } });
            setLogs(res.data.content);
            setTotalPages(res.data.totalPages);
            setPage(res.data.number);
            setSelectedIds(new Set());
        } catch (err) {
            console.error("Greška pri učitavanju SMS istorije", err);
        }
    };

    useEffect(() => {
        fetchPage(0);
    }, []);

    const refreshStatuses = async () => {
        setRefreshing(true);
        setRefreshResult(null);
        try {
            const res = await API.post('/sms-logs/refresh-statuses');
            setRefreshResult(`Provereno: ${res.data.checked}, ažurirano: ${res.data.updated}`);
            fetchPage(page);
        } catch (err) {
            setRefreshResult("Greška pri osvežavanju statusa.");
        } finally {
            setRefreshing(false);
        }
    };

    const toggleSelected = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const retryOne = async (id) => {
        setRetryingId(id);
        setRetryResult(null);
        try {
            await API.post('/sms-logs/retry', { ids: [id] });
            setRetryResult('Poruka ponovo poslata.');
            fetchPage(page);
        } catch (err) {
            setRetryResult('Greška pri ponovnom slanju.');
        } finally {
            setRetryingId(null);
        }
    };

    const retrySelected = async () => {
        if (selectedIds.size === 0) return;
        setRetryingSelected(true);
        setRetryResult(null);
        try {
            const res = await API.post('/sms-logs/retry', { ids: Array.from(selectedIds) });
            setRetryResult(`Ponovo poslato: ${res.data.retried}`);
            fetchPage(page);
        } catch (err) {
            setRetryResult('Greška pri ponovnom slanju.');
        } finally {
            setRetryingSelected(false);
        }
    };

    const formatDateTime = (isoString) => {
        return new Date(isoString).toLocaleString('sr-RS', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Poslate poruke</h2>

            <div style={styles.refreshBox}>
                <button style={{ ...styles.refreshBtn, ...(refreshing ? styles.refreshBtnDisabled : {}) }} onClick={refreshStatuses} disabled={refreshing}>
                    {refreshing ? 'Proveravanje...' : 'Osveži statuse'}
                </button>
                {refreshResult && <span style={styles.refreshResult}>{refreshResult}</span>}
            </div>
            <p style={styles.hint}>
                "Poslato" znači da je poruka predata Infobipu, ali se još ne zna da li je stigla na telefon -
                kliknite "Osveži statuse" da se proveri stvarna isporuka (dostupno za poruke poslate u poslednja 48h).
                Za poruke sa statusom "Greška" možete pokušati ponovo, pojedinačno ili više odjednom.
            </p>

            {selectedIds.size > 0 && (
                <div style={styles.retryBar}>
                    <span>{selectedIds.size} {selectedIds.size === 1 ? 'izabrana poruka' : 'izabranih poruka'}</span>
                    <button style={styles.retryBtn} onClick={retrySelected} disabled={retryingSelected}>
                        {retryingSelected ? 'Slanje...' : 'Pošalji ponovo izabrane'}
                    </button>
                </div>
            )}
            {retryResult && <div style={styles.retryResult}>{retryResult}</div>}

            <div style={styles.list}>
                {logs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                        Nema poslatih poruka.
                    </div>
                ) : (
                    logs.map(l => (
                        <div key={l.id} style={styles.row}>
                            <div style={styles.rowHeader}>
                                <div style={styles.recipientRow}>
                                    {isFailed(l.status) && (
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(l.id)}
                                            onChange={() => toggleSelected(l.id)}
                                        />
                                    )}
                                    <div style={styles.recipient}>{l.patientName} <span style={styles.phone}>({l.phoneNumber})</span></div>
                                </div>
                                <div style={styles.badges}>
                                    <span style={typeStyle}>{l.type === 'NOTIFICATION' ? 'Obaveštenje' : 'Podsetnik'}</span>
                                    <span style={channelStyle(l.channel)}>{l.channel === 'VIBER' ? 'Viber' : 'SMS'}</span>
                                    <span style={statusStyle(l.status)} title={l.status === 'SENT' ? 'Predato Infobipu, čeka se potvrda isporuke' : undefined}>
                                        {(STATUS_INFO[l.status] || STATUS_INFO.FAILED).label}
                                    </span>
                                </div>
                            </div>
                            <div style={styles.message}>{l.message}</div>
                            <div style={styles.rowFooter}>
                                <div style={styles.time}>{formatDateTime(l.sentAt)}</div>
                                {isFailed(l.status) && (
                                    <button
                                        style={styles.retryOneBtn}
                                        onClick={() => retryOne(l.id)}
                                        disabled={retryingId === l.id}
                                    >
                                        {retryingId === l.id ? 'Slanje...' : 'Pokušaj ponovo'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div style={styles.pagination}>
                    <button
                        style={{ ...styles.pageBtn, ...(page === 0 ? styles.pageBtnDisabled : {}) }}
                        onClick={() => fetchPage(page - 1)}
                        disabled={page === 0}
                    >
                        ‹ Prethodna
                    </button>
                    <span style={styles.pageInfo}>Strana {page + 1} od {totalPages}</span>
                    <button
                        style={{ ...styles.pageBtn, ...(page >= totalPages - 1 ? styles.pageBtnDisabled : {}) }}
                        onClick={() => fetchPage(page + 1)}
                        disabled={page >= totalPages - 1}
                    >
                        Sledeća ›
                    </button>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    title: { margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' },
    hint: { fontSize: '12px', color: '#94a3b8', marginBottom: '15px', lineHeight: '1.4' },
    refreshBox: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
    refreshBtn: { padding: '9px 16px', backgroundColor: '#1c8aa6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    refreshBtnDisabled: { backgroundColor: '#8fd0dc', cursor: 'not-allowed' },
    refreshResult: { fontSize: '13px', color: '#1f2937', fontWeight: '600' },
    list: { display: 'flex', flexDirection: 'column', gap: '10px' },
    row: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '12px 15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    },
    rowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
    recipientRow: { display: 'flex', alignItems: 'center', gap: '8px' },
    rowFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' },
    badges: { display: 'flex', gap: '6px' },
    recipient: { fontSize: '15px', fontWeight: '600', color: '#1f2937' },
    retryBar: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px',
        padding: '10px 14px', marginBottom: '10px', fontSize: '13px', fontWeight: '600', color: '#9a3412'
    },
    retryBtn: { padding: '8px 14px', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    retryOneBtn: { padding: '4px 10px', backgroundColor: '#f1f5f9', color: '#ea580c', border: '1px solid #fdba74', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' },
    retryResult: { fontSize: '13px', color: '#1f2937', fontWeight: '600', marginBottom: '10px' },
    phone: { fontWeight: 'normal', color: '#7f8c8d', fontSize: '13px' },
    message: { fontSize: '13px', color: '#475569', lineHeight: '1.4', marginBottom: '6px' },
    time: { fontSize: '12px', color: '#94a3b8' },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px' },
    pageBtn: { padding: '8px 14px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
    pageBtnDisabled: { backgroundColor: '#bdc3c7', cursor: 'not-allowed' },
    pageInfo: { fontSize: '14px', color: '#1f2937' }
};

export default SmsLogPanel;
